import React, { useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import WithProcessError from 'hocs/WithProcessError';
import PropTypes from 'prop-types';

import FormGroup from 'components/FormGroup/FormGroup';
import FormItem from 'components/FormItem/FormItem';
import IconButton from 'components/IconButton/IconButton';
import Input from 'components/Input/Input';
import Panel from 'components/Panel/Panel';
import PanelHeader from 'components/PanelHeader/PanelHeader';

import Check24 from 'icons/Check24';
import Close24 from 'icons/Close24';

import useApi from 'utils/api';
import { useAuth } from 'utils/auth';
import { validateName } from 'utils/validate';

function AppCreateTelegram(props) {
    const api = useApi();
    const auth = useAuth();
    const queryClient = useQueryClient();

    const { panelWidth, onBackClick, onCreated, processError } = props;
    const nameRef = useRef();

    const [token, setToken] = React.useState(null);
    const [error, setError] = React.useState(null);
    const [loading, setLoading] = React.useState(false);

    /**
     * Generates app token which will be used on app creation.
     */
    function prepareToken() {
        const companyId = auth?.company.id;
        setLoading(true);
        api.createApplicationToken(companyId)
            .then((result) => {
                const { app_key } = result;
                setToken(app_key);
            })
            .catch((error) => {
                processError('Не удалось получить токен приложения', error);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function getAppName() {
        return nameRef.current?.value.trim();
    }

    /**
     * Validate creation form fields.
     */
    function validateForm() {
        const name = getAppName();
        const error = validateName(name);
        setError(error);

        return error;
    }

    /**
     * Handle done click and create new application.
     */
    function handleDone() {
        const errors = validateForm();

        if (errors === null && token !== null) {
            const companyId = auth?.company.id;
            const params = {
                app_name: getAppName(),
                token,
                type: 'telegram',
            };
            setLoading(true);
            api.createApplication(companyId, params)
                .then((result) => {
                    if (result.application && typeof onCreated === 'function') {
                        queryClient.invalidateQueries({ queryKey: ['applications', 'company-' + companyId] });
                        onCreated();
                    }
                })
                .catch((error) => {
                    processError('Не удалось создать приложение', error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    React.useEffect(() => {
        prepareToken();
    }, []);

    return (
        <Panel
            contentStyles={{ minWidth: panelWidth, maxWidth: panelWidth, padding: '0 0.5rem' }}
            header={
                <PanelHeader
                    title={'Новое приложение'}
                    back={true}
                    backIcon={<Close24 />}
                    onBackClick={onBackClick}
                    after={<IconButton loading={loading} icon={<Check24 />} onClick={handleDone} accent={true} />}
                />
            }
            separated={true}
        >
            <FormGroup
                title={'Оповещения через Telegram'}
                description={'Бот Dispurse будет отправлять уведомления о платежах по вашим страницам оплаты.'}
            >
                <FormItem label={'Название приложения'} required={true} error={error}>
                    <Input ref={nameRef} name={'app-name'} type={'text'} placeholder={'Например, «Общий чат»'} />
                </FormItem>
            </FormGroup>
        </Panel>
    );
}

AppCreateTelegram.propTypes = {
    panelWidth: PropTypes.number,
    onBackClick: PropTypes.func,
    onCreated: PropTypes.func,
    processError: PropTypes.func,
};

export default WithProcessError(AppCreateTelegram);
